package com.exura.pages.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.userprofile.domain.model.DoctorProfile
import com.exura.features.userprofile.domain.model.OrganizationProfile
import com.exura.features.userprofile.domain.model.ProviderProfile
import com.exura.features.userprofile.domain.usecase.GetProviderProfileUseCase
import com.exura.features.userprofile.domain.usecase.SaveProviderProfileUseCase
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

sealed interface ProfileSettingsEvent {
    data object LoadProfile : ProfileSettingsEvent
    data class EditField(val fieldName: String) : ProfileSettingsEvent
    data class UpdateFieldValue(val fieldName: String, val value: String) : ProfileSettingsEvent
    data class SaveField(
        val fieldName: String,
        val onSuccess: (String) -> Unit,
        val onFailure: (String) -> Unit
    ) : ProfileSettingsEvent
    data object CancelEdit : ProfileSettingsEvent
}

data class ProfileSettingsUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val profile: ProviderProfile? = null,
    val editingField: String? = null,
    val editedValues: Map<String, String> = emptyMap()
)

class ProviderProfileSettingsController(
    private val scope: CoroutineScope,
    private val context: PageContext,
    private val getProviderProfileUseCase: GetProviderProfileUseCase,
    private val saveProviderProfileUseCase: SaveProviderProfileUseCase,
) {
    var uiState by mutableStateOf(ProfileSettingsUiState())
        private set

    init {
        loadProfile()
    }

    fun loadProfile() {
        scope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            getProviderProfileUseCase()
                .onSuccess { profile ->
                    val initialValues = buildMap {
                        when (profile) {
                            is DoctorProfile -> {
                                put("fullName", profile.fullName ?: "")
                                put("specialization", profile.specialization ?: "")
                            }
                            is OrganizationProfile -> {
                                put("name", profile.name ?: "")
                                put("field", profile.field ?: "")
                                put("contactNumber", profile.patientsContactNumber ?: "")
                            }
                        }
                    }
                    uiState = uiState.copy(
                        isLoading = false,
                        profile = profile,
                        editedValues = initialValues
                    )
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
        }
    }

    fun onEditField(fieldName: String) {
        uiState = uiState.copy(editingField = fieldName)
    }

    fun onUpdateFieldValue(fieldName: String, value: String) {
        uiState = uiState.copy(
            editedValues = uiState.editedValues + (fieldName to value)
        )
    }

    fun onSaveField(fieldName: String, onSuccess: (String) -> Unit, onFailure: (String) -> Unit) {
        val currentProfile = uiState.profile ?: return
        val newValue = uiState.editedValues[fieldName] ?: return

        val updatedProfile = when (currentProfile) {
            is DoctorProfile -> {
                when (fieldName) {
                    "fullName" -> currentProfile.copy(fullName = newValue)
                    "specialization" -> currentProfile.copy(specialization = newValue)
                    else -> currentProfile
                }
            }
            is OrganizationProfile -> {
                when (fieldName) {
                    "name" -> currentProfile.copy(name = newValue)
                    "field" -> currentProfile.copy(field = newValue)
                    "contactNumber" -> currentProfile.copy(patientsContactNumber = newValue)
                    else -> currentProfile
                }
            }
            else -> throw Exception()
        }

        scope.launch {
            uiState = uiState.copy(isLoading = true)
            saveProviderProfileUseCase(updatedProfile)
                .onSuccess {
                    uiState = uiState.copy(
                        isLoading = false,
                        profile = updatedProfile,
                        editingField = null
                    )
                    onSuccess("Profile updated successfully")
                }
                .onFailure { error ->
                    uiState = uiState.copy(isLoading = false)
                    onFailure(error.message ?: "Unknown error")
                }
        }
    }

    fun onCancelEdit() {
        val currentProfile = uiState.profile ?: return
        val resetValues = buildMap {
            when (currentProfile) {
                is DoctorProfile -> {
                    put("fullName", currentProfile.fullName ?: "")
                    put("specialization", currentProfile.specialization ?: "")
                }
                is OrganizationProfile -> {
                    put("name", currentProfile.name ?: "")
                    put("field", currentProfile.field ?: "")
                    put("contactNumber", currentProfile.patientsContactNumber ?: "")
                }
            }
        }
        uiState = uiState.copy(
            editingField = null,
            editedValues = resetValues
        )
    }

    fun handleEvent(event: ProfileSettingsEvent) {
        when (event) {
            is ProfileSettingsEvent.LoadProfile -> loadProfile()
            is ProfileSettingsEvent.EditField -> onEditField(event.fieldName)
            is ProfileSettingsEvent.UpdateFieldValue -> onUpdateFieldValue(event.fieldName, event.value)
            is ProfileSettingsEvent.SaveField -> onSaveField(event.fieldName, event.onSuccess, event.onFailure)
            is ProfileSettingsEvent.CancelEdit -> onCancelEdit()
        }
    }
}

@Composable
fun rememberProviderProfileSettingsController(
    scope: CoroutineScope,
    context: PageContext,
): ProviderProfileSettingsController {
    val getProviderProfile = injectAndRemember<GetProviderProfileUseCase>()
    val saveProviderProfile = injectAndRemember<SaveProviderProfileUseCase>()

    return remember(scope, context) {
        ProviderProfileSettingsController(
            scope = scope,
            context = context,
            getProviderProfileUseCase = getProviderProfile,
            saveProviderProfileUseCase = saveProviderProfile,
        )
    }
}